<template>
  <div ref="viewerContainer" class="fpv-container">
    <div v-if="$store.forgeStore.state.loadingToken" class="fpv-viewer-overlay">
      <v-progress-circular :size="40" :width="2" color="white" indeterminate />
      <div class="mt-3 white--text text-caption">Cargando Autodesk Forge...</div>
    </div>
    <div v-else-if="loadingModel" class="fpv-viewer-overlay">
      <v-progress-circular :size="40" :width="2" color="white" indeterminate />
      <div class="mt-3 white--text text-caption">Cargando modelo...</div>
    </div>
    <div :id="viewerDivId" class="fpv-viewer"></div>
    <div class="fpv-label-layer" aria-hidden="true">
      <span
        v-for="label in polygonLabelPositions"
        :key="label.id"
        :class="['fpv-polygon-label', { 'fpv-polygon-label--current': label.isCurrent }]"
        :style="{ left: `${label.left}px`, top: `${label.top}px` }"
      >{{ label.text }}</span>
    </div>
  </div>
</template>

<script>
const VIEWER_JS_URL  = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js'
const VIEWER_CSS_URL = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css'

function loadViewerResources () {
  return new Promise((resolve, reject) => {
    if (document.getElementById('forge-viewer-script')) { resolve(); return }
    if (!document.getElementById('forge-viewer-css')) {
      const link = document.createElement('link')
      link.id  = 'forge-viewer-css'
      link.rel = 'stylesheet'
      link.href = VIEWER_CSS_URL
      document.head.appendChild(link)
    }
    const script = document.createElement('script')
    script.id      = 'forge-viewer-script'
    script.src     = VIEWER_JS_URL
    script.onload  = () => resolve()
    script.onerror = () => reject(new Error('No se pudo cargar el viewer de Autodesk'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'ForgePolygonViewer',

  props: {
    plano: {
      type: Object,
      default: null
    },
    planoData: {
      type: Object,
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    allPolygons: {
      type: Array,
      default: () => []
    },
    currentPolygonLabel: {
      type: String,
      default: null
    },
    forgeFileUrn: {
      type: String,
      default: null
    },
    lineColor: {
      type: String,
      default: '#2D4B95'
    },
    fillOpacity: {
      type: Number,
      default: 0.3
    },
    settingPolygon: {
      type: Boolean,
      default: true
    },
    // Cambiar este valor (ej. un contador o timestamp) desde el padre cierra el
    // polígono en curso, como alternativa a llamar finishPolygonDrawing() por ref.
    closeSignal: {
      type: [Number, String, Boolean],
      default: null
    }
  },

  data () {
    return {
      selectedForgeFile: null,
      forgeSearch: null,
      forgeViewer: null,
      viewerInitialized: false,
      viewerReady: false,
      loadingModel: false,
      edit2d: null,
      edit2dToolsRegistered: false,
      activeToolMode: 'none',
      hasMarkupData: false,
      loading: false,
      polygonLabelPositions: [],
      cameraChangeHandler: null
    }
  },

  computed: {
    viewerDivId () {
      return `forgeViewer-reusable-${this._uid}`
    },
    isReadOnly () {
      return this.readOnly
    },
    isAllPolygonsMode () {
      return this.readOnly && this.allPolygons && this.allPolygons.length > 0
    },
    forgeFiles () {
      return this.$store.forgeStore.state.forgeFiles || []
    },
    // La navegación (zoom/pan/orbit) sólo se bloquea en dispositivos táctiles, donde el
    // gesto de touch se reemite como eventos de mouse sintéticos para el dibujo de
    // polígonos (ver _initTouchSupport). En escritorio el mouse real coexiste sin
    // problema con el polygonTool de Edit2D, así que la navegación nativa queda intacta.
    isTouchDevice () {
      return typeof window !== 'undefined' && (
        ('ontouchstart' in window) ||
        (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
        (typeof navigator !== 'undefined' && navigator.msMaxTouchPoints > 0)
      )
    }
  },

  watch: {
    planoData: {
      immediate: true,
      handler () {
        this.syncSelectedForgeFileFromPlano()
      }
    },

    plano: {
      immediate: true,
      handler () {
        this.syncSelectedForgeFileFromPlano()
      }
    },

    '$store.forgeStore.state.loadingToken' (loading) {
      if (!loading && !this.viewerInitialized) {
        this.bootViewer()
      }
    },

    forgeFiles () {
      this.syncSelectedForgeFileFromPlano()
    },

    lineColor () {
      this.applyStyleToContext()
      this.applyStyleToExistingShapes()
    },

    fillOpacity () {
      this.applyStyleToContext()
      this.applyStyleToExistingShapes()
    },

    forgeFileUrn (newUrn) {
      if (newUrn && this.viewerInitialized) {
        this._loadForgeByUrn(newUrn)
      }
    },

    allPolygons () {
      if (this.viewerReady) {
        this.restorePolygonsWhenReady()
      }
    },

    currentPolygonLabel () {
      if (this.viewerReady) this.updatePolygonLabels()
    },

    settingPolygon () {
      if (!this.viewerInitialized) return
      this._syncInteractionMode()
    },

    activeToolMode (newMode, oldMode) {
      // Nueva sesión de dibujo → olvidar dónde estaba el primer punto anterior
      if (newMode === 'draw' && oldMode !== 'draw') {
        this._polygonFirstTouchPoint = null
      }
    },

    closeSignal (val, oldVal) {
      if (val === null || val === undefined || val === oldVal) return
      this._finishActivePolygon()
    }
  },

  mounted () {
    const projectId = this.$store.projectsStore.state.selectedProject &&
      this.$store.projectsStore.state.selectedProject.id
    this.$store.forgeStore.dispatch('fetchForgeToken', ['data:read', 'bucket:read'])
    if (projectId) {
      this.$store.forgeStore.dispatch('fetchForgeFiles', { project_id: projectId })
    }
    this.$nextTick(() => {
      if (!this.$store.forgeStore.state.loadingToken) {
        this.bootViewer()
      }
    })
  },

  beforeDestroy () {
    this.destroyViewer()
		const el = document.getElementById(this.viewerDivId)
		if (el) delete el.__forgeViewerInstance
  },

  methods: {
    async bootViewer () {
      if (this.viewerInitialized) return
      try {
        await loadViewerResources()
        this.initializeViewer()
      } catch (e) {
        console.error('[ForgePolygonViewer] Error cargando recursos del viewer:', e)
      }
    },

    initializeViewer () {
      // eslint-disable-next-line no-undef
      if (typeof Autodesk === 'undefined') return

      // eslint-disable-next-line no-undef
      Autodesk.Viewing.Initializer(
        { env: 'AutodeskProduction2', api: 'streamingV2', getAccessToken: this.getForgeToken },
        async () => {
          const div = document.getElementById(this.viewerDivId)
          if (!div) return

          // eslint-disable-next-line no-undef
          this.forgeViewer = new Autodesk.Viewing.Private.GuiViewer3D(div, {
            extensions: ['Autodesk.DocumentBrowser', 'Autodesk.Measure', 'Autodesk.ViewCubeUi']
          })

          if (this.forgeViewer.start() > 0) {
            console.error('[ForgePolygonViewer] Error al iniciar el viewer')
            return
          }

          this.viewerInitialized = true
          this._applyLightBackground()
          this._bindCameraChange()
          this.edit2d = await this.forgeViewer.loadExtension('Autodesk.Edit2D')
					this._exposeViewerOnEl()
          this._syncInteractionMode()

          if (this.forgeFileUrn) {
            this._loadForgeByUrn(this.forgeFileUrn)
          } else if (this.selectedForgeFile) {
            this.loadForgeModel(this.selectedForgeFile)
          }
        }
      )
    },

		_exposeViewerOnEl () {
			const el = document.getElementById(this.viewerDivId)
			if (el) el.__forgeViewerInstance = this.forgeViewer
		},

    _bindCameraChange () {
      if (!this.forgeViewer || typeof this.forgeViewer.addEventListener !== 'function') return
      // eslint-disable-next-line no-undef
      const eventName = Autodesk.Viewing.CAMERA_CHANGE_EVENT
      this.cameraChangeHandler = () => this.updatePolygonLabels()
      this.forgeViewer.addEventListener(eventName, this.cameraChangeHandler)
    },

    _unbindCameraChange () {
      if (!this.forgeViewer || !this.cameraChangeHandler || typeof this.forgeViewer.removeEventListener !== 'function') {
        this.cameraChangeHandler = null
        return
      }
      // eslint-disable-next-line no-undef
      this.forgeViewer.removeEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this.cameraChangeHandler)
      this.cameraChangeHandler = null
    },

    updatePolygonLabels () {
      if (!this.forgeViewer || !this.viewerReady || typeof this.forgeViewer.worldToClient !== 'function') {
        this.polygonLabelPositions = []
        return
      }

      const Vector3 = this.getForgeVector3()
      if (!Vector3) {
        this.polygonLabelPositions = []
        return
      }

      const viewerElement = document.getElementById(this.viewerDivId)
      const containerElement = this.$refs && this.$refs.viewerContainer
      const viewerRect = viewerElement && viewerElement.getBoundingClientRect
        ? viewerElement.getBoundingClientRect()
        : { left: 0, top: 0 }
      const containerRect = containerElement && containerElement.getBoundingClientRect
        ? containerElement.getBoundingClientRect()
        : { left: 0, top: 0 }
      const offsetX = viewerRect.left - containerRect.left
      const offsetY = viewerRect.top - containerRect.top

      const polygons = [...(this.allPolygons || [])]
      if (this.planoData && this.planoData.nodes && this.planoData.nodes.length > 0 && this.currentPolygonLabel) {
        polygons.push({
          id: `current-${this.planoData.id || 'polygon'}`,
          nodes: this.planoData.nodes,
          label: this.currentPolygonLabel,
          isCurrent: true
        })
      }

      this.polygonLabelPositions = polygons
        .filter(item => item && item.nodes && item.nodes.length > 0)
        .map(item => {
          const center = item.nodes.reduce((accumulator, node) => ({
            x: accumulator.x + node.x,
            y: accumulator.y + node.y
          }), { x: 0, y: 0 })
          center.x /= item.nodes.length
          center.y /= item.nodes.length

          try {
            const clientPoint = this.forgeViewer.worldToClient(new Vector3(center.x, center.y, 0))
            if (!clientPoint || !Number.isFinite(clientPoint.x) || !Number.isFinite(clientPoint.y)) return null
            return {
              id: item.id,
              text: item.label || (item.number != null ? `Nº${item.number}` : ''),
              isCurrent: item.isCurrent === true,
              left: clientPoint.x + offsetX,
              top: clientPoint.y + offsetY
            }
          } catch (error) {
            return null
          }
        })
        .filter(item => item && Number.isFinite(item.left) && Number.isFinite(item.top))
    },

    getForgeVector3 () {
      // eslint-disable-next-line no-undef
      if (typeof THREE !== 'undefined' && THREE.Vector3) return THREE.Vector3
      // eslint-disable-next-line no-undef
      if (typeof Autodesk !== 'undefined' && Autodesk.Viewing && Autodesk.Viewing.Private && Autodesk.Viewing.Private.THREE) {
        // eslint-disable-next-line no-undef
        return Autodesk.Viewing.Private.THREE.Vector3
      }
      return null
    },

    getForgeToken (cb) {
      cb(this.$store.forgeStore.state.forgeToken, 3600)
    },

    // Fuerza un fondo claro sólido usando la API oficial del viewer, independiente del
    // tema claro/oscuro del sistema/navegador que el viewer aplica por defecto a través
    // de sus variables CSS. Se llama tras el arranque y tras cada carga de documento,
    // ya que algunos documentos (especialmente hojas 2D) pueden reestablecer el fondo
    // al cargar.
    _applyLightBackground () {
      if (!this.forgeViewer) return
      try {
        this.forgeViewer.setBackgroundColor(255, 255, 255, 255, 255, 255)
      } catch (e) {
        console.warn('[ForgePolygonViewer] No se pudo fijar el fondo claro:', e)
      }
    },

    destroyViewer () {
      this._unbindCameraChange()
      this.polygonLabelPositions = []
      this.stopShapeWatcher()
      this.setToolMode('none')
      this._destroyTouchSupport()
      if (this.forgeViewer) {
        this._setSelectionLocked(false)
        this._setNavigationLocked(false)
        try { this.forgeViewer.finish() } catch (e) { /* silencioso */ }
        this.forgeViewer           = null
        this.edit2d                = null
        this.edit2dToolsRegistered = false
        this.viewerInitialized     = false
        this.viewerReady           = false
        this.loadingModel          = false
        this.activeToolMode        = 'none'
        this.hasMarkupData         = !!(this.planoData && this.planoData.nodes && this.planoData.nodes.length > 0)
        // eslint-disable-next-line no-undef
        try { Autodesk.Viewing.shutdown() } catch (e) { /* silencioso */ }
      }
    },

    _loadForgeByUrn (urn) {
      this.viewerReady  = false
      this.loadingModel = true
      const encodedUrn  = btoa(urn)

      // eslint-disable-next-line no-undef
      Autodesk.Viewing.Document.load(
        'urn:' + encodedUrn,
        async (doc) => {
          const node = doc.getRoot().getDefaultGeometry()
          await this.forgeViewer.loadDocumentNode(doc, node)
          this._ensureEdit2dTools()
          this._applyLightBackground()
          this.loadingModel = false
          this.viewerReady  = true
          this.restorePolygonsWhenReady()
          this._syncInteractionMode()
        },
        (code, msg) => {
          console.error('[ForgePolygonViewer] Error cargando documento Forge:', code, msg)
          this.loadingModel = false
        }
      )
    },

    _ensureEdit2dTools () {
      if (this.edit2d && !this.edit2dToolsRegistered) {
        try {
          this.edit2d.registerDefaultTools()
          this.edit2dToolsRegistered = true
        } catch (e) {
          console.warn('[ForgePolygonViewer] Edit2D registerDefaultTools:', e)
        }
      }
    },

    syncSelectedForgeFileFromPlano () {
      const selectedFileId =
        (this.plano && this.plano.file && this.plano.file.id) ||
        (this.plano && this.plano.file_id) ||
        (this.planoData && this.planoData.forgeFileId)

      if (!selectedFileId) {
        if (!this.forgeFileUrn && !this.isAllPolygonsMode) {
          this.selectedForgeFile = null
        }
        return
      }

      if (!this.forgeFiles || this.forgeFiles.length === 0) return

      const selectedFileIdStr = String(selectedFileId)
      const found = this.forgeFiles.find(f => {
        const fileId = (f && f.id != null) ? String(f.id) : null
        const nestedFileId = (f && f.file_id != null) ? String(f.file_id) : null
        const nestedForgeFileId = (f && f.forge_file && f.forge_file.id != null)
          ? String(f.forge_file.id)
          : null
        return fileId === selectedFileIdStr || nestedFileId === selectedFileIdStr || nestedForgeFileId === selectedFileIdStr
      })
      if (!found) return

      const currentId = this.selectedForgeFile && this.selectedForgeFile.id != null
        ? String(this.selectedForgeFile.id)
        : null
      const hasChanged = currentId !== String(found.id)
      this.selectedForgeFile = found

      if (hasChanged && this.viewerInitialized && !this.loadingModel && !this.forgeFileUrn) {
        this.loadForgeModel(found)
      }
    },

    loadForgeModel (file) {
      if (!file || !file.forge_file || !file.forge_file.urn) return
      this.viewerReady  = false
      this.loadingModel = true
      const urn = btoa(file.forge_file.urn)

      // eslint-disable-next-line no-undef
      Autodesk.Viewing.Document.load(
        'urn:' + urn,
        async (doc) => {
          const node = doc.getRoot().getDefaultGeometry()
          await this.forgeViewer.loadDocumentNode(doc, node)
          this._ensureEdit2dTools()
          this._applyLightBackground()
          this.loadingModel = false
          this.viewerReady  = true

          this.restorePolygonsWhenReady()
          this._syncInteractionMode()
        },
        (code, msg) => {
          console.error('[ForgePolygonViewer] Error cargando documento Forge:', code, msg)
          this.loadingModel = false
        }
      )
    },

    setToolMode (mode) {
      if (!this.edit2d || !this.forgeViewer) { this.activeToolMode = 'none'; return }

      const tools      = this.edit2d.defaultTools
      const controller = this.forgeViewer.toolController
      this.stopShapeWatcher()

      const getActiveName = () => {
        try { return controller.getActiveToolName ? controller.getActiveToolName() : '' } catch (e) { return '' }
      }
      const polygonToolName = tools && tools.polygonTool     ? tools.polygonTool.getName()     : null
      const editToolName    = tools && tools.polygonEditTool ? tools.polygonEditTool.getName() : null

      if (mode === 'none') {
        if (polygonToolName) {
          try { controller.deactivateTool(polygonToolName) } catch (e) { /* ignorado */ }
        }
        if (editToolName) {
          try { controller.deactivateTool(editToolName) } catch (e) { /* ignorado */ }
        }
        this._polygonFirstTouchPoint = null
        this.activeToolMode = 'none'
        return
      }

      if (mode === 'draw') {
        if (!polygonToolName) { this.activeToolMode = 'none'; return }

        const existingShapes = this.edit2d.defaultContext &&
          this.edit2d.defaultContext.layer &&
          (this.edit2d.defaultContext.layer.shapes || []).filter(s => s != null && !s._laotHistorical)
        if (existingShapes && existingShapes.length > 0) {
          return this.setToolMode('edit')
        }

        const active = getActiveName()
        if (active && active !== polygonToolName) {
          try { controller.deactivateTool(active) } catch (e) { /* ignorado */ }
        }
        this.activeToolMode = 'draw'
        this.applyStyleToContext()
        this.applyStyleToTool(tools.polygonTool)
        try {
          controller.activateTool(polygonToolName)
        } catch (e) {
          this.activeToolMode = 'none'
          return
        }
        this.startShapeWatcher()
      } else if (mode === 'edit') {
        if (!editToolName) { this.activeToolMode = 'none'; return }

        const active = getActiveName()
        if (polygonToolName) {
          try { controller.deactivateTool(polygonToolName) } catch (e) { /* ignorado */ }
        }
        if (active === editToolName) {
          this.activeToolMode = 'edit'
          this.applyStyleToExistingShapes()
          return
        }
        this.activeToolMode = 'edit'
        try {
          controller.activateTool(editToolName)
        } catch (e) {
          this.activeToolMode = 'none'
        }
      }
    },

    _syncInteractionMode () {
      if (!this.viewerInitialized || !this.forgeViewer || !this.edit2d) return

      const canEdit = !this.isReadOnly && this.settingPolygon
      if (!canEdit) {
        this.setToolMode('none')
        this._destroyTouchSupport()
        this._setNavigationLocked(false)
        this._setSelectionLocked(false)
        return
      }

      this._initTouchSupport()
      this._setNavigationLocked(true)
      this._setSelectionLocked(true)

      if (!this.viewerReady) return

      const layer = this.edit2d.defaultContext && this.edit2d.defaultContext.layer
      const shapes = layer && layer.shapes ? layer.shapes.filter(shape => shape != null && !shape._laotHistorical) : []
      this.setToolMode(shapes.length > 0 ? 'edit' : 'draw')
    },

    applyStyleToContext () {
      if (!this.edit2d || !this.edit2d.defaultContext) return
      const ctx = this.edit2d.defaultContext
      if (!ctx.style) return
      this._applyStyleToObj(ctx.style)
    },

    applyStyleToTool (tool) {
      if (!tool) return
      if (tool.style) {
        this._applyStyleToObj(tool.style)
      } else if (tool.setStyle) {
        const s = {}
        this._applyStyleToObj(s)
        tool.setStyle(s)
      }
    },

    _applyStyleToObj (obj) {
      this._applyStyle(obj, this.lineColor, this.fillOpacity)
    },

    _applyStyle (obj, color, opacity) {
      obj.lineColor = color
      obj.lineWidth = 2
      obj.lineAlpha = 1
      obj.fillColor = color
      obj.fillAlpha = opacity
    },

    applyStyleToExistingShapes () {
      if (!this.edit2d || !this.edit2d.defaultContext) return
      const layer = this.edit2d.defaultContext.layer
      if (!layer || !layer.shapes || layer.shapes.length === 0) return
      layer.shapes.forEach(shape => {
        if (shape && shape.style) this._applyStyleToObj(shape.style)
      })
      layer.update()
    },

    startShapeWatcher () {
      if (!this.edit2d || !this.edit2d.defaultContext) return
      const layer    = this.edit2d.defaultContext.layer
      const polyTool = this.edit2d.defaultTools && this.edit2d.defaultTools.polygonTool
      if (!layer || !polyTool) return

      let triggered = false
      const patchedMethods = []

      const onPolygonCommitted = () => {
        if (triggered || this._restoringMarkup) return
        triggered = true
        this.stopShapeWatcher()
        setTimeout(() => {
          const shapes = (layer.shapes || []).filter(s => s != null)
          if (shapes.length > 0) {
            this.hasMarkupData = true
            if (this.settingPolygon && !this.isReadOnly) {
              this.setToolMode('edit')
            } else {
              this.setToolMode('none')
            }
            const ctx = this.edit2d && this.edit2d.defaultContext
            if (ctx && ctx.selection && shapes[0]) {
              try { ctx.selection.selectOnly(shapes[0]) } catch (e) { /* ignorado */ }
            }
            shapes.forEach(shape => {
              if (shape && shape.style) this._applyStyleToObj(shape.style)
            })
            layer.update()
            // Se cierra el polígono — sin importar si fue por Enter (desktop), doble
            // click, tap sobre el primer nodo (móvil), o finishPolygonDrawing()/closeSignal
            // llamado desde el padre.
            this.$emit('polygon-closed', {
              nodes:   this.extractPolygonNodes(),
              color:   this.lineColor,
              opacity: this.fillOpacity
            })
          } else {
            if (this.settingPolygon && !this.isReadOnly) {
              triggered = false
              this.startShapeWatcher()
            }
          }
        }, 400)
      }

      const tryPatch = (name) => {
        if (typeof polyTool[name] !== 'function') return false
        const orig = polyTool[name].bind(polyTool)
        polyTool[name] = (...args) => {
          const result = orig(...args)
          onPolygonCommitted(name)
          return result
        }
        patchedMethods.push({ name, orig })
        return true
      }

      tryPatch('finishPolygon')
      tryPatch('handleDoubleClick')
      tryPatch('handleDoubleTap')
      tryPatch('handleKeyDown')

      this._restorePolyToolMethods = () => {
        patchedMethods.forEach(({ name, orig }) => { polyTool[name] = orig })
        this._restorePolyToolMethods = null
      }
    },

    stopShapeWatcher () {
      if (this._restorePolyToolMethods) this._restorePolyToolMethods()
      if (this._addShapeRestore) { this._addShapeRestore(); this._addShapeRestore = null }
      if (this._shapeWatchInterval) { clearInterval(this._shapeWatchInterval); this._shapeWatchInterval = null }
    },

    _clearLayer () {
      if (!this.edit2d) return
      const layer = this.edit2d.defaultContext && this.edit2d.defaultContext.layer
      if (!layer) return
      const historicalShapes = (layer.shapes || []).filter(shape => shape && shape._laotHistorical)
      if (typeof layer.clear === 'function') layer.clear()
      historicalShapes.forEach(shape => layer.addShape(shape))
      layer.update()
    },

    _clearAllLayer () {
      if (!this.edit2d) return
      const layer = this.edit2d.defaultContext && this.edit2d.defaultContext.layer
      if (layer) {
        layer.clear()
        layer.update()
      }
    },

    restorePolygonsWhenReady (attempt = 0) {
      this.$nextTick(() => {
        const layer = this.edit2d && this.edit2d.defaultContext && this.edit2d.defaultContext.layer
        if (!this.viewerReady || !layer) {
          if (attempt < 5) {
            setTimeout(() => this.restorePolygonsWhenReady(attempt + 1), 50)
          }
          return
        }

        this._clearAllLayer()
        this.restoreAllPolygons()
        if (this.planoData && this.planoData.nodes && this.planoData.nodes.length > 0) {
          this._restoreFromNodes(this.planoData.nodes, this.planoData.color, this.planoData.opacity)
        }
        this.updatePolygonLabels()
      })
    },

    clearMarkup () {
      if (!this.edit2d) return
      this.stopShapeWatcher()
      this._clearLayer()
      this.hasMarkupData = false
      this._polygonFirstTouchPoint = null
      this.$emit('save-plano', null)
      this.$nextTick(() => this.setToolMode(this.settingPolygon && !this.isReadOnly ? 'draw' : 'none'))
    },

    extractPolygonNodes () {
      if (!this.edit2d) return []
      const layer = this.edit2d.defaultContext.layer
      if (!layer || !layer.shapes || layer.shapes.length === 0) return []
      const shape = layer.shapes.find(s => s != null && !s._laotHistorical)
      if (!shape || !shape.points) return []
      const nodes = []
      for (let i = 0; i < shape.points.length; i++) {
        nodes.push({ x: shape.points[i].x, y: shape.points[i].y })
      }
      return nodes
    },

    _restoreFromNodes (nodes, color, opacity, isHistorical = false) {
      if (!this.edit2d || !nodes || nodes.length < 2) return
      try {
        const layer = this.edit2d.defaultContext.layer
        if (!layer) return
        const effectiveColor   = color   !== undefined ? color   : this.lineColor
        const effectiveOpacity = opacity !== undefined ? opacity : this.fillOpacity
        this._restoringMarkup = true
        try {
          // eslint-disable-next-line no-undef
          const poly = new Autodesk.Edit2D.Polygon()
          nodes.forEach(pt => poly.addPoint(pt.x, pt.y))
          poly._laotHistorical = isHistorical
          poly.selectable = !isHistorical
          poly.editable = !isHistorical
          poly.locked = isHistorical
          if (poly.style) this._applyStyle(poly.style, effectiveColor, effectiveOpacity)
          layer.addShape(poly)
        } finally {
          this._restoringMarkup = false
        }
        layer.update()
        if (!isHistorical) this.hasMarkupData = true
        if (!isHistorical && !this.isReadOnly && this.settingPolygon) {
          setTimeout(() => {
            if (this.settingPolygon && !this.isReadOnly) this.setToolMode('edit')
          }, 300)
        }
      } catch (e) {
        console.error('[ForgePolygonViewer] Error restaurando nodos:', e)
      }
    },

    restoreAllPolygons () {
      if (!this.edit2d || !this.allPolygons || this.allPolygons.length === 0) return
      this.allPolygons.forEach(item => {
        if (item.nodes && item.nodes.length > 0) {
          this._restoreFromNodes(item.nodes, item.color || '#2D4B95', item.opacity != null ? item.opacity : 0.3, true)
        }
      })
    },

    async savePlano () {
      if (!this.hasMarkupData) return
      this.loading = true
      try {
        const nodes = this.extractPolygonNodes()
        this.$emit('save-plano', {
          nodes,
          color:   this.lineColor,
          opacity: this.fillOpacity
        })
        this.showSuccessMessage('Emplazamiento guardado correctamente')
      } catch (error) {
        this.showErrorMessage('Error al guardar el emplazamiento')
        console.error(error)
      } finally {
        this.loading = false
      }
    },

    showSuccessMessage (message, title = 'Éxito') {
      this.$store.globalStore.dispatch('addMessage', {
        message, title, created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
      })
    },

    showErrorMessage (message) {
      this.$store.globalStore.dispatch('addMessage', {
        message, title: 'Error', created: new Date(), type: 'error', icon: 'mdi-alert-circle', color: '#fb8c00'
      })
    },

    // Llamable desde el padre vía ref: this.$refs.miViewer.finishPolygonDrawing()
    finishPolygonDrawing () {
      this._finishActivePolygon()
    },

    _finishActivePolygon () {
      if (this.activeToolMode !== 'draw') return
      const tool = this.edit2d && this.edit2d.defaultTools && this.edit2d.defaultTools.polygonTool
      if (tool && typeof tool.finishPolygon === 'function') {
        try { tool.finishPolygon() } catch (e) { console.warn('[ForgePolygonViewer] Error al cerrar el polígono:', e) }
      }
      this._polygonFirstTouchPoint = null
    },

    _setNavigationLocked (locked) {
      if (!this.forgeViewer) return
      try {
        if (typeof this.forgeViewer.setNavigationLock === 'function') {
          this.forgeViewer.setNavigationLock(locked)
        }
      } catch (e) {
        console.warn('[ForgePolygonViewer] No se pudo cambiar el bloqueo de navegación:', e)
      }
    },

    _setSelectionLocked (locked) {
      if (!this.forgeViewer) return
      try {
        if (locked) {
          this.forgeViewer.clearSelection()
          if (!this._selectionLockedListener) {
            this._selectionLockedListener = () => { this.forgeViewer && this.forgeViewer.clearSelection() }
            // eslint-disable-next-line no-undef
            this.forgeViewer.addEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this._selectionLockedListener)
          }
          try { this.forgeViewer.impl.disableHighlight(true) } catch (e) { /* no disponible en esta versión */ }
        } else {
          if (this._selectionLockedListener) {
            // eslint-disable-next-line no-undef
            this.forgeViewer.removeEventListener(Autodesk.Viewing.SELECTION_CHANGED_EVENT, this._selectionLockedListener)
            this._selectionLockedListener = null
          }
          try { this.forgeViewer.impl.disableHighlight(false) } catch (e) { /* no disponible en esta versión */ }
        }
      } catch (e) {
        console.warn('[ForgePolygonViewer] No se pudo cambiar el bloqueo de selección:', e)
      }
    },

    // ── Touch support ──
    // En desktop el viewer "simplemente funciona" porque escucha eventos NATIVOS de
    // mouse (mousedown/mousemove/mouseup/click/dblclick) sobre el canvas y es el
    // propio ToolController de Autodesk el que decide cómo traducirlos (click discreto
    // vs. arrastre, selección, etc). Intentar llamar a mano los métodos internos del
    // tool (handleButtonDown/handleButtonUp) no reproduce esa lógica interna y termina
    // en comportamientos incorrectos (rectángulo en vez de polígono, o nada).
    //
    // La solución robusta es no tocar los tools directamente: en su lugar, se disparan
    // eventos MouseEvent reales sobre el canvas a partir del touch, para que corra
    // exactamente el mismo pipeline que ya funciona en escritorio.

    _dispatchMouseEvent (type, touch, opts = {}) {
      const viewerDiv = document.getElementById(this.viewerDivId)
      const canvas    = viewerDiv ? viewerDiv.querySelector('canvas') : null
      if (!canvas) return
      const evt = new MouseEvent(type, {
        bubbles:    true,
        cancelable: true,
        composed:   true,
        view:       window,
        clientX:    touch.clientX,
        clientY:    touch.clientY,
        screenX:    touch.screenX,
        screenY:    touch.screenY,
        button:     0,
        buttons:    opts.buttons !== undefined ? opts.buttons : 0,
        detail:     opts.detail  !== undefined ? opts.detail  : 1
      })
      canvas.dispatchEvent(evt)
    },

    _initTouchSupport () {
      const viewerDiv = document.getElementById(this.viewerDivId)
      if (!viewerDiv || this._touchListeners) return

      let lastTapTime  = 0
      let lastTapX     = 0
      let lastTapY     = 0
      let touchMoved   = false
      let touchStartX  = 0
      let touchStartY  = 0

      const MOVE_THRESHOLD     = 12   // px — más tolerante que mouse; el dedo tiembla al tocar
      const DOUBLE_TAP_MS      = 350
      const DOUBLE_TAP_DIST    = 25
      const CLOSE_TO_FIRST_DIST = 28  // px — radio de tap para "cerrar" tocando el primer nodo

      const isToolActive = () => this.activeToolMode === 'draw' || this.activeToolMode === 'edit'

      const onTouchStart = (e) => {
        if (e.touches.length !== 1 || !isToolActive()) return   // dejar pinch/pan al viewer nativo
        e.preventDefault()
        e.stopPropagation() // tomamos control total del gesto, sin doble manejo del viewer
        const t     = e.touches[0]
        touchMoved  = false
        touchStartX = t.clientX
        touchStartY = t.clientY

        this._dispatchMouseEvent('mousemove', t, { buttons: 0 })
        this._dispatchMouseEvent('mousedown', t, { buttons: 1, detail: 1 })
      }

      const onTouchMove = (e) => {
        if (e.touches.length !== 1 || !isToolActive()) return
        e.preventDefault()
        e.stopPropagation()
        const t = e.touches[0]
        if (
          Math.abs(t.clientX - touchStartX) > MOVE_THRESHOLD ||
          Math.abs(t.clientY - touchStartY) > MOVE_THRESHOLD
        ) {
          touchMoved = true
        }
        this._dispatchMouseEvent('mousemove', t, { buttons: 1 })
      }

      const onTouchEnd = (e) => {
        if (e.changedTouches.length !== 1 || !isToolActive()) return
        e.preventDefault()
        e.stopPropagation()
        const t = e.changedTouches[0]

        this._dispatchMouseEvent('mousemove', t, { buttons: 1 })
        this._dispatchMouseEvent('mouseup',   t, { buttons: 0 })

        if (this.activeToolMode !== 'draw') {
          // Modo edición: click simple (selección/soltar vértice), sin lógica de cierre
          this._dispatchMouseEvent('click', t, { buttons: 0, detail: 1 })
          touchMoved = false
          return
        }

        // Doble tap → equivalente a un doble click real (cierra el polígono)
        const now  = Date.now()
        const doubleTapDist = Math.sqrt(
          Math.pow(t.clientX - lastTapX, 2) + Math.pow(t.clientY - lastTapY, 2)
        )
        const isDoubleTap = !touchMoved && (now - lastTapTime) < DOUBLE_TAP_MS && doubleTapDist < DOUBLE_TAP_DIST

        // Tap sobre el primer nodo ya colocado → cerrar el polígono, más intuitivo que
        // el doble tap en móvil (equivalente a presionar Enter en escritorio)
        let isCloseToFirst = false
        if (!touchMoved && this._polygonFirstTouchPoint) {
          const firstDist = Math.sqrt(
            Math.pow(t.clientX - this._polygonFirstTouchPoint.x, 2) +
            Math.pow(t.clientY - this._polygonFirstTouchPoint.y, 2)
          )
          isCloseToFirst = firstDist < CLOSE_TO_FIRST_DIST
        }

        const shouldClose = isDoubleTap || isCloseToFirst

        this._dispatchMouseEvent('click', t, { buttons: 0, detail: shouldClose ? 2 : 1 })

        if (shouldClose) {
          this._dispatchMouseEvent('dblclick', t, { buttons: 0, detail: 2 })
          lastTapTime = 0
          this._polygonFirstTouchPoint = null
        } else {
          lastTapTime = now
          lastTapX    = t.clientX
          lastTapY    = t.clientY
          // Este tap agregó un vértice nuevo — si es el primero de la sesión, recordar
          // su posición en pantalla para detectar el cierre por proximidad más adelante
          if (!this._polygonFirstTouchPoint) {
            this._polygonFirstTouchPoint = { x: t.clientX, y: t.clientY }
          }
        }
        touchMoved = false
      }

      const onTouchCancel = (e) => {
        if (!isToolActive() || !e.changedTouches || e.changedTouches.length !== 1) {
          touchMoved = false
          return
        }
        const t = e.changedTouches[0]
        // Soltar el botón para no dejar el pipeline de mouse "presionado" a medias
        this._dispatchMouseEvent('mouseup', t, { buttons: 0 })
        touchMoved = false
      }

      // capture: true → nuestros handlers corren antes que los del viewer, y
      // stopPropagation() evita que el touch nativo del viewer también reaccione
      // (paneo/orbit) mientras estamos dibujando o editando un polígono.
      viewerDiv.addEventListener('touchstart',  onTouchStart,  { capture: true, passive: false })
      viewerDiv.addEventListener('touchmove',   onTouchMove,   { capture: true, passive: false })
      viewerDiv.addEventListener('touchend',    onTouchEnd,    { capture: true, passive: false })
      viewerDiv.addEventListener('touchcancel', onTouchCancel, { capture: true, passive: false })

      this._touchListeners = { viewerDiv, onTouchStart, onTouchMove, onTouchEnd, onTouchCancel }
    },

    _destroyTouchSupport () {
      if (!this._touchListeners) return
      const { viewerDiv, onTouchStart, onTouchMove, onTouchEnd, onTouchCancel } = this._touchListeners
      viewerDiv.removeEventListener('touchstart',  onTouchStart,  { capture: true })
      viewerDiv.removeEventListener('touchmove',   onTouchMove,   { capture: true })
      viewerDiv.removeEventListener('touchend',    onTouchEnd,    { capture: true })
      viewerDiv.removeEventListener('touchcancel', onTouchCancel, { capture: true })
      this._touchListeners = null
    }
  }
}
</script>

<style scoped>
.fpv-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #1a1a2e;
  display: flex;
  flex-direction: column;
}

.fpv-viewer {
  width: 100%;
  flex: 1;
}

.fpv-label-layer {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
  overflow: hidden;
}

.fpv-polygon-label {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 2px 5px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.9);
  color: #2d4b95;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.fpv-polygon-label--current {
  background: rgba(220, 245, 228, 0.95);
  color: #1b5e20;
}

.fpv-viewer-overlay {
  position: absolute; inset: 0;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  background: rgba(26,26,46,0.85);
  z-index: 10; pointer-events: none;
}
</style>